import { makeAutoObservable, runInAction } from 'mobx'
import { decorate, inject, injectable } from '@evertel/di'
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'
import { PollConfig } from '@evertel/session/PollConfig'
import { BlueUserStore } from '../store'
import { UserSettingsStore } from '@evertel/stores'
import { DepartmentsAccessStore } from '@evertel/departments-access'
import { DepartmentStore } from '@evertel/department'
import { differenceBy } from 'lodash'
import { getPrimaryDepartmentsAccess } from '@evertel/utils'

const CURRENT_USER_POLL_INTERVAL_MS = 1000 * 25 // 25 seconds

class CurrentUserPollService {
    private pollInterval = undefined // holds the setInterval timer

    constructor(
        private api: Api,
        private sessionService: SessionService,
        private userStore: BlueUserStore,
        private userSettingsStore: UserSettingsStore,
        private departmentsAccessStore: DepartmentsAccessStore,
        private departmentStore: DepartmentStore
    ) {
        makeAutoObservable(this)
    }

    startPoll = () => {
        this.stopPoll()
        if (!PollConfig.globalEnabled) return

        this.pollInterval = setInterval(() => {
            this.fetchUserDetails()
        }, CURRENT_USER_POLL_INTERVAL_MS)
    }

    stopPoll = () => {
        if (this.pollInterval) {
            clearInterval(this.pollInterval)
            this.pollInterval = undefined
        }
    }

    // Extend the interval timer if its set
    resetPollInterval = () => {
        if (this.pollInterval) {
            this.startPoll()
        }
    }

    async fetchUserDetails() {
        this.resetPollInterval()
        if (!this.sessionService.currentUserId) return

        // This is a superset to DEFAULT_USER_SCOPE
        const userDetails = await this.api.Routes.BlueUser.getById(this.sessionService.currentUserId, {
            include: [{
                relation: 'publicMedia'
            }, {
                relation: 'departmentsAccess',
                scope: {
                    include: [{
                        relation: 'department',
                        scope: {
                            include: ['publicMedia']
                        }
                    }]
                }
            }]
        })

        // issues making this `relation: 'settings'` due to it being in the hidden list and not wanting to include it in other requests
        const userSettings = await this.api.Routes.BlueUser.getSettings(this.sessionService.currentUserId)

        runInAction(() => {
            // Store this user
            this.userStore.update(userDetails)
            this.userSettingsStore.update({ id: this.sessionService.currentUserId, settings: userSettings })
            this.departmentsAccessStore.update(userDetails.departmentsAccess)
            this.departmentStore.update(userDetails.departmentsAccess.map(da => da.department))

            // We need to delete any departments access that doesn't exist in the remote data, but we have locally
            const localDa = this.departmentsAccessStore.find(o => o.blueUserId === userDetails.id)
            const deleteDa = differenceBy(localDa, userDetails.departmentsAccess, 'id')

            for (const da of deleteDa) {
                this.departmentsAccessStore.deleteById(da.id)
            }

            // remove the selected department if the user was removed from department
            // also auto-select department if user had none at all
            const availableDeptIds = userDetails.departmentsAccess.map(da => da.departmentId)
            if (!availableDeptIds.includes(this.sessionService.selectedDepartmentId)) {
                const nextDeparmentId = getPrimaryDepartmentsAccess(userDetails.departmentsAccess)?.departmentId
                this.sessionService.setSelectedDeparmentId(nextDeparmentId)
            }
        })
    }
}

let i = 0
decorate(injectable(), CurrentUserPollService)
decorate(inject(Api), CurrentUserPollService, i++)
decorate(inject(SessionService), CurrentUserPollService, i++)
decorate(inject(BlueUserStore), CurrentUserPollService, i++)
decorate(inject(UserSettingsStore), CurrentUserPollService, i++)
decorate(inject(DepartmentsAccessStore), CurrentUserPollService, i++)
decorate(inject(DepartmentStore), CurrentUserPollService, i++)

export { CurrentUserPollService }
